import React, { useState } from 'react';
import { Mail, Phone, Globe, MessageCircle, Ruler, Home, Award, Users, ChevronRight, Menu, X } from 'lucide-react';
import { products, contactInfo, features } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const iconMap = {
  Ruler: Ruler,
  Home: Home,
  Award: Award,
  Users: Users
};

const HomePage = () => {
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    const subject = encodeURIComponent(`Contact Form - ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`
    );
    
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
    
    toast({
      title: "Opening Email Client",
      description: "Your email application will open with the message.",
    });
    
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/80 backdrop-blur-sm z-50 transition-all duration-300">
        <div className="container mx-auto px-4 py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="text-2xl font-light tracking-wide text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                North Lumé
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-10">
              <button onClick={() => scrollToSection('home')} className="text-white/90 hover:text-white transition-colors duration-200 text-sm font-normal">Home</button>
              <button onClick={() => scrollToSection('products')} className="text-white/90 hover:text-white transition-colors duration-200 text-sm font-normal">Catalog</button>
              <button onClick={() => scrollToSection('features')} className="text-white/90 hover:text-white transition-colors duration-200 text-sm font-normal">Gallery</button>
              <button onClick={() => scrollToSection('contact')} className="text-white/90 hover:text-white transition-colors duration-200 text-sm font-normal">Contact</button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-white/90 hover:text-white transition-colors duration-200">Home</button>
              <button onClick={() => scrollToSection('products')} className="block w-full text-left text-white/90 hover:text-white transition-colors duration-200">Products</button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left text-white/90 hover:text-white transition-colors duration-200">Features</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-white/90 hover:text-white transition-colors duration-200">Contact</button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="home" 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1722763529109-2bcb289a47c3?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Subtle overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8">
              <h1 className="text-5xl md:text-7xl font-normal leading-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Elevate Your Space with
                <span className="block mt-2">Timeless Elegance</span>
              </h1>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto font-light">
                Discover our curated collection of premium window treatments, crafted for the discerning eye.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-6 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white hover:bg-neutral-100 text-black transition-all duration-300 text-base px-8 py-6 font-normal"
                  onClick={() => scrollToSection('products')}
                >
                  Explore Catalog
                  <ChevronRight className="ml-2" size={20} />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white backdrop-blur-sm transition-all duration-300 text-base px-8 py-6 font-normal"
                  onClick={() => scrollToSection('contact')}
                >
                  Get Consultation
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="text-white/80 rotate-90" size={28} />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-neutral-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">Why Choose North Lumé</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Experience unparalleled service and craftsmanship with every installation
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
                  <CardHeader>
                    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
                      <IconComponent className="text-amber-700" size={32} />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">Our Premium Collection</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Discover our exquisite range of window treatments, each designed to elevate your living spaces
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={`${product.image}?w=600&q=80`}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl">{product.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-base leading-relaxed">{product.description}</CardDescription>
                  <div className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-neutral-600">
                        <ChevronRight size={16} className="text-amber-600" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 border-amber-600 text-amber-700 hover:bg-amber-50 transition-colors duration-200"
                    onClick={() => scrollToSection('contact')}
                  >
                    Request Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-neutral-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Ready to transform your space? Contact us for a free consultation and estimate
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-none shadow-xl bg-white">
              <CardHeader>
                <CardTitle className="text-2xl">Send us a Message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name" 
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com" 
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (123) 456-7890"
                      className="transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message" 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project..."
                      rows={5}
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-neutral-900 hover:bg-neutral-800 text-white transition-all duration-300 transform hover:scale-105"
                    size="lg"
                  >
                    Send Message
                    <Mail className="ml-2" size={20} />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <Card className="border-none shadow-xl bg-white hover:shadow-2xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl">Contact Information</CardTitle>
                  <CardDescription>Reach us through any of these channels</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors duration-200">
                      <Mail className="text-amber-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 mb-1">Email</div>
                      <div className="text-neutral-600">{contactInfo.email}</div>
                    </div>
                  </a>

                  <a 
                    href={`tel:${contactInfo.phone}`}
                    className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors duration-200">
                      <Phone className="text-amber-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 mb-1">Phone</div>
                      <div className="text-neutral-600">{contactInfo.phoneDisplay}</div>
                    </div>
                  </a>

                  <a 
                    href={`https://${contactInfo.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors duration-200">
                      <Globe className="text-amber-700" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 mb-1">Website</div>
                      <div className="text-neutral-600">{contactInfo.website}</div>
                    </div>
                  </a>

                  <a 
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start space-x-4 p-4 rounded-lg hover:bg-neutral-50 transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition-colors duration-200">
                      <MessageCircle className="text-green-600" size={24} />
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 mb-1">WhatsApp</div>
                      <div className="text-neutral-600">Click to chat with us</div>
                    </div>
                  </a>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl bg-gradient-to-br from-amber-50 to-amber-100/50">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-neutral-900">Special Offer</h3>
                    <p className="text-neutral-700 leading-relaxed">
                      <span className="font-semibold">Free measurements and estimate</span> for all custom window treatment installations. Schedule your consultation today!
                    </p>
                    <Button 
                      className="w-full bg-amber-700 hover:bg-amber-800 text-white transition-all duration-300"
                      onClick={() => window.open(`https://wa.me/${contactInfo.whatsapp}`, '_blank')}
                    >
                      Book Free Consultation
                      <ChevronRight className="ml-2" size={20} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">NORTH LUMÉ</h3>
              <p className="text-neutral-400 mb-4">Blinds · Shutters · Shades</p>
              <p className="text-neutral-400">Elegant Window Solutions</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('home')} className="block text-neutral-400 hover:text-white transition-colors duration-200">Home</button>
                <button onClick={() => scrollToSection('products')} className="block text-neutral-400 hover:text-white transition-colors duration-200">Products</button>
                <button onClick={() => scrollToSection('features')} className="block text-neutral-400 hover:text-white transition-colors duration-200">Features</button>
                <button onClick={() => scrollToSection('contact')} className="block text-neutral-400 hover:text-white transition-colors duration-200">Contact</button>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <div className="space-y-2 text-neutral-400">
                <p>Custom Blinds Installation</p>
                <p>Free Measurements</p>
                <p>Free Estimate</p>
                <p>Professional Consultation</p>
              </div>
            </div>
          </div>
          <div className="border-t border-neutral-800 pt-8 text-center text-neutral-400">
            <p>&copy; {new Date().getFullYear()} North Lumé. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
